import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Key, LogIn, User, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { ForgotPasswordDialog } from './ForgotPasswordDialog';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});

type LoginFormValues = z.infer<typeof loginSchema>;

type LoginFormProps = {
  defaultEmail: string;
  defaultPassword: string;
  onSubmit: (data: LoginFormValues) => void;
  isLoading: boolean;
  userRole: string;
}

const LoginForm = ({
  defaultEmail,
  defaultPassword,
  onSubmit,
  isLoading,
  userRole
}: LoginFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: defaultEmail,
      password: defaultPassword
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input 
                    className="pl-12 h-12 text-base rounded-xl border-gray-300 focus:border-hospital-primary focus:ring-hospital-primary" 
                    placeholder="Enter your email" 
                    type="email" 
                    autoComplete="username"
                    {...field} 
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Key className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input 
                    className="pl-12 pr-12 h-12 text-base rounded-xl border-gray-300 focus:border-hospital-primary focus:ring-hospital-primary" 
                    placeholder="Enter your password" 
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    {...field}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 h-5 w-5 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex items-center justify-end pt-2">
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-hospital-primary hover:text-hospital-primary/80 font-medium"
          >
            Forgot password?
          </button>
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-hospital-primary hover:bg-hospital-primary/90 h-12 text-base font-semibold rounded-xl shadow-lg" 
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center">
              <span className="mr-2">Signing in</span>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            </div>
          ) : (
            <div className="flex items-center">
              <LogIn className="mr-2 h-4 w-4" />
              <span>Sign in</span>
            </div>
          )}
        </Button>
      </form>
      
      <ForgotPasswordDialog 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </Form>
  );
};

export default LoginForm;
